import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../../auth/useAuth';
import { useFiscalYears, useCreateFiscalYear, useUpdateFiscalYear } from '../../hooks/useFiscalYears';
import { LoadingSpinner } from '../../components/shared/LoadingSpinner';
import { ErrorMessage } from '../../components/shared/ErrorMessage';
import { FiscalYearDto } from '../../api/fiscalYears';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridOptions } from 'ag-grid-community';

interface CreateFiscalYearFormData {
  fiscalYearCode: string;
  fiscalYearDesc: string;
  isActive: boolean;
}

interface EditFiscalYearFormData {
  fiscalYearDesc: string;
  isActive: boolean;
}

interface FiscalYearRow {
  fiscalYearId: number;
  fiscalYearCode: string;
  fiscalYearDesc?: string;
  isActive: boolean;
  createdBy: number;
  createdByName: string;
  createdOn: string;
}

const filterLabelStyle = (isSelected: boolean): React.CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  cursor: 'pointer',
  padding: '5px 4px',
  fontSize: '0.875rem',
  fontWeight: isSelected ? 600 : 400,
  color: '#333',
});

export function MastersPage() {
  const auth = useAuth();
  const isAdmin = auth.roles.includes('BRG.Admin');
  
  const fiscalYearsQuery = useFiscalYears();
  const createMutation = useCreateFiscalYear();
  const updateMutation = useUpdateFiscalYear();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingFiscalYear, setEditingFiscalYear] = useState<FiscalYearDto | null>(null);
  const [formData, setFormData] = useState<CreateFiscalYearFormData>({
    fiscalYearCode: '',
    fiscalYearDesc: '',
    isActive: true,
  });
  const [editFormData, setEditFormData] = useState<EditFiscalYearFormData>({
    fiscalYearDesc: '',
    isActive: true,
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formValidationErrors, setFormValidationErrors] = useState<{ [key: string]: string }>({});
  const [selectedFilter, setSelectedFilter] = useState('Fiscal Year');

  const isLoading = fiscalYearsQuery.isLoading;
  const hasError = fiscalYearsQuery.isError;
  const errorMsg = fiscalYearsQuery.error?.message ?? 'Failed to load fiscal years.';

  const handleCreateClick = useCallback(() => {
    setShowCreateModal(true);
    setSuccessMessage('');
    setErrorMessage('');
    setFormValidationErrors({});
    setFormData({ fiscalYearCode: '', fiscalYearDesc: '', isActive: true });
  }, []);

  const handleEditClick = useCallback((fiscalYear: FiscalYearDto) => {
    setEditingFiscalYear(fiscalYear);
    setEditFormData({
      fiscalYearDesc: fiscalYear.fiscalYearDesc || '',
      isActive: fiscalYear.isActive,
    });
    setSuccessMessage('');
    setErrorMessage('');
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setFormData({ fiscalYearCode: '', fiscalYearDesc: '', isActive: true });
    setFormValidationErrors({});
    setSuccessMessage('');
    setErrorMessage('');
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditingFiscalYear(null);
    setEditFormData({ fiscalYearDesc: '', isActive: true });
    setSuccessMessage('');
    setErrorMessage('');
  }, []);

  const handleInputChange = useCallback(
    (field: keyof CreateFiscalYearFormData, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear validation error for this field when user starts typing
      if (formValidationErrors[field]) {
        setFormValidationErrors((prev) => {
          const copy = { ...prev };
          delete copy[field];
          return copy;
        });
      }
    },
    [formValidationErrors]
  );

  const handleEditInputChange = useCallback(
    (field: keyof EditFiscalYearFormData, value: any) => {
      setEditFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setErrorMessage('');
      setSuccessMessage('');
      setFormValidationErrors({});

      const trimmedCode = formData.fiscalYearCode.trim();
      const newErrors: { [key: string]: string } = {};

      // Validate required fields
      if (!trimmedCode) {
        newErrors.fiscalYearCode = 'Fiscal Year Code is required.';
      } else {
        // Validate format: FY YYYY (e.g., FY 2028)
        const fyFormatRegex = /^FY\s\d{4}$/;
        if (!fyFormatRegex.test(trimmedCode)) {
          newErrors.fiscalYearCode = 'Fiscal Year Code must be in "FY YYYY" format (e.g., FY 2028).';
        }
      }

      // If there are validation errors, display them and return
      if (Object.keys(newErrors).length > 0) {
        setFormValidationErrors(newErrors);
        return;
      }

      createMutation.mutate(
        {
          fiscalYearCode: trimmedCode,
          fiscalYearDesc: formData.fiscalYearDesc.trim(),
        },
        {
          onSuccess: () => {
            setSuccessMessage(`Fiscal Year "${trimmedCode}" created successfully!`);
            setFormData({ fiscalYearCode: '', fiscalYearDesc: '', isActive: true });
            setShowCreateModal(false);
            setTimeout(() => setSuccessMessage(''), 4000);
          },
          onError: (error: any) => {
            const message =
              error.response?.data?.message || error.message || 'Failed to create fiscal year.';
            setErrorMessage(message);
          },
        }
      );
    },
    [formData.fiscalYearCode, createMutation]
  );

  const handleEditSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!editingFiscalYear) return;

      setErrorMessage('');
      setSuccessMessage('');

      updateMutation.mutate(
        {
          id: editingFiscalYear.fiscalYearId,
          request: {
            fiscalYearDesc: editFormData.fiscalYearDesc.trim(),
            isActive: editFormData.isActive,
          },
        },
        {
          onSuccess: () => {
            setSuccessMessage(`Fiscal Year "${editingFiscalYear.fiscalYearCode}" updated successfully!`);
            setEditingFiscalYear(null);
            setEditFormData({ fiscalYearDesc: '', isActive: true });
            setTimeout(() => setSuccessMessage(''), 4000);
          },
          onError: (error: any) => {
            const message =
              error.response?.data?.message || error.message || 'Failed to update fiscal year.';
            setErrorMessage(message);
          },
        }
      );
    },
    [editingFiscalYear, editFormData, updateMutation]
  );

  // Custom CSS for AG Grid styling (matching Members page style)
  const customGridStyles = `
    .ag-theme-quartz {
      --ag-header-background-color: #1a3bcc;
      --ag-header-foreground-color: #fff;
      --ag-header-cell-text-color: #fff;
      --ag-header-cell-padding: 8px 10px;
      --ag-header-cell-hover-background-color: #1530a8;
      --ag-header-cell-moving-background-color: #1530a8;
      --ag-font-size: 11px;
      --ag-cell-horizontal-padding: 10px;
      --ag-cell-vertical-padding: 8px;
      --ag-row-height: 32px;
      --ag-row-border-color: #f0f0f0;
      background-color: #ffffff;
      border: none;
      border-radius: 0;
    }
    
    .ag-theme-quartz .ag-header {
      font-weight: 700;
      font-size: 11px;
      height: 32px;
      background-color: #1a3bcc;
    }
    
    .ag-theme-quartz .ag-header-cell {
      background-color: #1a3bcc !important;
      color: #fff !important;
      border-right: 1px solid #1530a8;
      font-weight: 700;
    }
    
    .ag-theme-quartz .ag-header-cell:last-child {
      border-right: none;
    }
    
    .ag-theme-quartz .ag-row {
      border-bottom: 1px solid #f0f0f0;
      height: 32px;
      background-color: #ffffff;
    }
    
    .ag-theme-quartz .ag-row:last-child {
      border-bottom: none;
    }
    
    .ag-theme-quartz .ag-row:hover {
      background-color: #f7f9ff !important;
    }
    
    .ag-theme-quartz .ag-cell {
      font-size: 11px;
      color: #333;
      padding: 8px 10px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      display: flex;
      align-items: center;
    }
    
    .ag-theme-quartz .ag-root {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
    }
    
    .ag-theme-quartz .ag-root-wrapper {
      border: none;
    }
    
    .ag-theme-quartz .ag-center-cols-viewport {
      overflow-x: auto;
    }
  `;

  const sortedFiscalYears = useMemo(() => {
    if (!fiscalYearsQuery.data) return [];
    return [...fiscalYearsQuery.data].sort((a, b) =>
      b.fiscalYearCode.localeCompare(a.fiscalYearCode)
    );
  }, [fiscalYearsQuery.data]);

  if (!isAdmin) {
    return (
      <div style={{ width: '100%', padding: '24px 80px', maxWidth: 'none' }}>
        <ErrorMessage
          message="Only Admins can access this page."
          onRetry={() => (window.location.href = '/admin')}
        />
      </div>
    );
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading fiscal years..." fullPage />;
  }

  if (hasError) {
    return (
      <div style={{ width: '100%', padding: '24px 80px', maxWidth: 'none' }}>
        <ErrorMessage
          message={errorMsg}
          onRetry={() => {
            void fiscalYearsQuery.refetch();
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ width: '100%', padding: '24px 80px', maxWidth: 'none' }}>
      {/* Success Message */}
      {successMessage && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#f0f7f4',
            border: '1px solid #4caf50',
            borderRadius: '4px',
            marginBottom: '16px',
            color: '#2e7d32',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span style={{ fontSize: '1.1rem' }}>✓</span>
          {successMessage}
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div
          style={{
            padding: '12px 16px',
            backgroundColor: '#ffebee',
            border: '1px solid #d32f2f',
            borderRadius: '4px',
            marginBottom: '16px',
            color: '#c62828',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span style={{ fontSize: '1.1rem' }}>⚠</span>
          {errorMessage}
        </div>
      )}

      {/* Action Bar */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={handleCreateClick}
          disabled={showCreateModal || !!editingFiscalYear}
          style={{
            padding: '9px 20px',
            backgroundColor: showCreateModal || editingFiscalYear ? '#cccccc' : '#0078d4',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: showCreateModal || editingFiscalYear ? 'not-allowed' : 'pointer',
            fontWeight: 600,
            fontSize: '0.875rem',
            letterSpacing: '0.5px',
          }}
        >
          NEW YEAR
        </button>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateFiscalYearModal
          formData={formData}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
          onClose={handleCloseCreateModal}
          isPending={createMutation.isPending}
          validationErrors={formValidationErrors}
        />
      )}

      {/* Edit Modal */}
      {editingFiscalYear && (
        <EditFiscalYearModal
          fiscalYear={editingFiscalYear}
          formData={editFormData}
          onInputChange={handleEditInputChange}
          onSubmit={handleEditSubmit}
          onClose={handleCloseEditModal}
          isPending={updateMutation.isPending}
        />
      )}

      {/* Main Content: Grid + Sidebar */}
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        {/* Fiscal Years Grid */}
        <div style={{ flex: 1, minWidth: '0' }}>
          <FiscalYearsGrid
            fiscalYears={sortedFiscalYears}
            customGridStyles={customGridStyles}
            onYearCodeClick={handleEditClick}
          />
        </div>

        {/* Right Sidebar - Filter Container */}
        <div
          style={{
            width: '200px',
            flexShrink: 0,
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
            padding: '16px',
          }}
        >
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 700, color: '#1a1a1a' }}>
            View
          </h3>

          <label style={filterLabelStyle(selectedFilter === 'Fiscal Year')}>
            <input
              type="radio"
              name="masterFilter"
              value="Fiscal Year"
              checked={selectedFilter === 'Fiscal Year'}
              onChange={(e) => setSelectedFilter(e.target.value)}
              style={{ cursor: 'pointer' }}
            />
            Fiscal Year
          </label>
        </div>
      </div>
    </div>
  );
}

// Create Fiscal Year Modal Component
interface CreateFiscalYearModalProps {
  formData: CreateFiscalYearFormData;
  onInputChange: (field: keyof CreateFiscalYearFormData, value: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  isPending: boolean;
  validationErrors: { [key: string]: string };
}

function CreateFiscalYearModal({
  formData,
  onInputChange,
  onSubmit,
  onClose,
  isPending,
  validationErrors,
}: CreateFiscalYearModalProps) {
  // Check if form is complete to enable/disable create button
  const isFormComplete = formData.fiscalYearCode.trim() !== '' && !validationErrors.fiscalYearCode;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: '#1a3bcc',
            color: 'white',
            padding: '14px 18px',
            borderRadius: '8px 8px 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            position: 'sticky',
            top: 0,
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '14px', fontWeight: 800 }}>New Fiscal Year</h2>
            <div style={{ fontSize: '11px', opacity: 0.85, marginTop: '2px' }}>Add a new financial year to the system</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '18px',
              cursor: 'pointer',
              fontWeight: 700,
              padding: 0,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <form onSubmit={onSubmit} style={{ padding: '20px 24px' }}>
          {/* Fiscal Year Code */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
              Fiscal Year Code <span style={{ color: '#a01a23' }}>*</span>
            </label>
            <input
              type="text"
              value={formData.fiscalYearCode}
              onChange={(e) => onInputChange('fiscalYearCode', e.target.value)}
              placeholder="e.g., FY 2028"
              style={{
                width: '100%',
                padding: '7px 10px',
                border: validationErrors.fiscalYearCode ? '2px solid #a01a23' : '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '12px',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
            {validationErrors.fiscalYearCode && (
              <p style={{ color: '#a01a23', fontSize: '11px', marginTop: '3px', margin: '3px 0 0 0' }}>
                {validationErrors.fiscalYearCode}
              </p>
            )}
          </div>

          {/* Description */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
              Description (Optional)
            </label>
            <input
              type="text"
              value={formData.fiscalYearDesc}
              onChange={(e) => onInputChange('fiscalYearDesc', e.target.value)}
              placeholder="e.g., Fiscal Year 2028"
              style={{
                width: '100%',
                padding: '7px 10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '12px',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Active Checkbox */}
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => onInputChange('isActive', e.target.checked)}
              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
            />
            <label style={{ fontSize: '11px', fontWeight: 700, color: '#444', cursor: 'pointer' }}>
              Active
            </label>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              style={{
                padding: '8px 16px',
                backgroundColor: '#999',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '11px',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isFormComplete || isPending}
              style={{
                padding: '8px 16px',
                backgroundColor: !isFormComplete || isPending ? '#999' : '#0078d4',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: !isFormComplete || isPending ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '11px',
              }}
            >
              {isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Edit Fiscal Year Modal Component
interface EditFiscalYearModalProps {
  fiscalYear: FiscalYearDto;
  formData: EditFiscalYearFormData;
  onInputChange: (field: keyof EditFiscalYearFormData, value: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  isPending: boolean;
}

function EditFiscalYearModal({
  fiscalYear,
  formData,
  onInputChange,
  onSubmit,
  onClose,
  isPending,
}: EditFiscalYearModalProps) {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '90%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: '#1a3bcc',
            color: 'white',
            padding: '14px 18px',
            borderRadius: '8px 8px 0 0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            position: 'sticky',
            top: 0,
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '14px', fontWeight: 800 }}>Edit Fiscal Year</h2>
            <div style={{ fontSize: '11px', opacity: 0.85, marginTop: '2px' }}>{fiscalYear.fiscalYearCode}</div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '18px',
              cursor: 'pointer',
              fontWeight: 700,
              padding: 0,
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <form onSubmit={onSubmit} style={{ padding: '20px 24px' }}>
          {/* Fiscal Year Code (Read-only) */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
              Fiscal Year Code
            </label>
            <div style={{
              width: '100%',
              padding: '7px 10px',
              backgroundColor: '#f5f5f5',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#666',
            }}>
              {fiscalYear.fiscalYearCode}
            </div>
          </div>

          {/* Description */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: '#444' }}>
              Description (Optional)
            </label>
            <input
              type="text"
              value={formData.fiscalYearDesc}
              onChange={(e) => onInputChange('fiscalYearDesc', e.target.value)}
              placeholder="e.g., Fiscal Year 2028"
              style={{
                width: '100%',
                padding: '7px 10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '12px',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
              }}
            />
          </div>

          {/* Active Checkbox */}
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => onInputChange('isActive', e.target.checked)}
              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
            />
            <label style={{ fontSize: '11px', fontWeight: 700, color: '#444', cursor: 'pointer' }}>
              Active
            </label>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              style={{
                padding: '8px 16px',
                backgroundColor: '#999',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '11px',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              style={{
                padding: '8px 16px',
                backgroundColor: isPending ? '#999' : '#0078d4',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                fontSize: '11px',
              }}
            >
              {isPending ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Fiscal Years Grid Component
interface FiscalYearsGridProps {
  fiscalYears: FiscalYearDto[];
  customGridStyles: string;
  onYearCodeClick: (fiscalYear: FiscalYearDto) => void;
}

interface FiscalYearRow {
  fiscalYearId: number;
  fiscalYearCode: string;
  fiscalYearDesc?: string;
  isActive: boolean;
  createdBy: number;
  createdByName: string;
  createdOn: string;
}

function FiscalYearsGrid({ fiscalYears, customGridStyles, onYearCodeClick }: FiscalYearsGridProps) {
  const gridRef = useRef<AgGridReact>(null);

  const columnDefs: ColDef<FiscalYearRow>[] = useMemo(
    () => [
      {
        field: 'isActive',
        headerName: 'Status',
        flex: 0.8,
        minWidth: 90,
        cellRenderer: (params: any) => {
          const isActive = params.value;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: isActive ? '#4caf50' : '#9e9e9e',
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: isActive ? '#2e7d32' : '#757575',
                }}
              >
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          );
        },
      },
      {
        field: 'fiscalYearCode',
        headerName: 'Year Code',
        flex: 0.9,
        minWidth: 100,
        cellRenderer: (params: any) => {
          const fiscalYear = fiscalYears.find(fy => fy.fiscalYearCode === params.value);
          return (
            <button
              onClick={() => fiscalYear && onYearCodeClick(fiscalYear)}
              style={{
                background: 'none',
                border: 'none',
                color: '#0078d4',
                fontWeight: 500,
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '11px',
                padding: 0,
              }}
            >
              {params.value}
            </button>
          );
        },
      },
      {
        field: 'fiscalYearDesc',
        headerName: 'Description',
        flex: 1.2,
        minWidth: 150,
        valueFormatter: (params) => params.value || '—',
      },
      {
        field: 'createdByName',
        headerName: 'Created By',
        flex: 0.9,
        minWidth: 100,
      },
      {
        field: 'createdOn',
        headerName: 'Created On',
        flex: 1,
        minWidth: 130,
        valueFormatter: (params) => {
          if (!params.value) return '—';
          const date = new Date(params.value);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          });
        },
      },
    ],
    [fiscalYears, onYearCodeClick]
  );

  const gridOptions: GridOptions<FiscalYearRow> = {
    domLayout: 'normal',
    rowHeight: 32,
    headerHeight: 32,
    suppressColumnVirtualisation: false,
    suppressCellFocus: true,
    suppressMovableColumns: true,
  };

  const rowData: FiscalYearRow[] = useMemo(
    () =>
      fiscalYears.map((fy) => ({
        fiscalYearId: fy.fiscalYearId,
        fiscalYearCode: fy.fiscalYearCode,
        fiscalYearDesc: fy.fiscalYearDesc,
        isActive: fy.isActive,
        createdBy: fy.createdBy,
        createdByName: fy.createdByName,
        createdOn: fy.createdOn,
      })),
    [fiscalYears]
  );

  return (
    <div
      style={{
        backgroundColor: '#fff',
        border: '1px solid #ddd',
        borderRadius: '6px',
        overflow: 'hidden',
        height: '500px',
      }}
    >
      <style>{customGridStyles}</style>
      {fiscalYears.length === 0 ? (
        <div
          style={{
            padding: '40px 16px',
            textAlign: 'center',
            color: '#999',
            fontSize: '0.9rem',
          }}
        >
          No fiscal years found. Click "NEW YEAR" to create one.
        </div>
      ) : (
        <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
          <AgGridReact<FiscalYearRow>
            ref={gridRef}
            columnDefs={columnDefs}
            rowData={rowData}
            gridOptions={gridOptions}
            defaultColDef={{
              resizable: false,
              sortable: false,
              filter: false,
            }}
          />
        </div>
      )}
    </div>
  );
}
